// The client's own carrier accounts — add, edit, delete (Stephanie
// 2026-10-30: there was no screen for them at all; the shipping windows only
// read them). Lives on the account's Addresses tab.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { CARRIER_OPTIONS, carrierLabel } from "@/lib/carriers";

interface ShippingAccountRow {
  id: string;
  carrier: string;
  accountNumber: string;
  accountPostalCode: string;
  label: string | null;
  isActive: boolean | null;
}

interface Draft {
  carrier: string;
  accountNumber: string;
  zip: string;
  label: string;
}

const EMPTY: Draft = { carrier: "ups", accountNumber: "", zip: "", label: "" };
const input = "mt-1 block h-9 w-full rounded-md border border-rule bg-white px-2 text-sm text-ink";

export function ClientShippingAccounts({ clientId, canEdit }: { clientId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const key = ["clients", clientId, "shipping-accounts"];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api<{ shipping_accounts: ShippingAccountRow[] }>(`/clients/${clientId}/shipping-accounts`),
    enabled: clientId.length > 0,
  });
  const [editing, setEditing] = useState<{ id: string | "new"; draft: Draft } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const done = (): void => {
    setEditing(null);
    setConfirmDelete(null);
    setError(null);
    void qc.invalidateQueries({ queryKey: key });
  };
  const save = useMutation({
    mutationFn: (e: { id: string | "new"; draft: Draft }) => {
      const body = {
        carrier: e.draft.carrier,
        account_number: e.draft.accountNumber.trim(),
        account_postal_code: e.draft.zip.trim(),
        label: e.draft.label.trim() || null,
      };
      return e.id === "new"
        ? api(`/clients/${clientId}/shipping-accounts`, { method: "POST", body: JSON.stringify(body) })
        : api(`/shipping-accounts/${e.id}`, { method: "PATCH", body: JSON.stringify(body) });
    },
    onSuccess: done,
    onError: (e) => setError(apiErrorMessage(e, "We couldn't save that account. Check the number and ZIP, then try again.")),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/shipping-accounts/${id}`, { method: "DELETE" }),
    onSuccess: done,
    onError: (e) => setError(apiErrorMessage(e, "We couldn't delete that account. Try again.")),
  });
  const rows = q.data?.shipping_accounts ?? [];

  const form = (e: { id: string | "new"; draft: Draft }) => (
    <form
      className="grid gap-3 rounded-md border border-rule bg-cream-50 p-4 sm:grid-cols-4"
      onSubmit={(ev) => {
        ev.preventDefault();
        if (!e.draft.accountNumber.trim() || e.draft.zip.trim().length < 3) {
          setError("Type the account number and the ZIP the account is billed to.");
          return;
        }
        save.mutate(e);
      }}
    >
      <label className="text-xs font-medium text-muted">
        Carrier
        <select value={e.draft.carrier} onChange={(v) => setEditing({ ...e, draft: { ...e.draft, carrier: v.target.value } })} className={input}>
          {CARRIER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </label>
      <label className="text-xs font-medium text-muted">
        Account number
        <input value={e.draft.accountNumber} onChange={(v) => setEditing({ ...e, draft: { ...e.draft, accountNumber: v.target.value } })} className={input} />
      </label>
      <label className="text-xs font-medium text-muted">
        Billing ZIP
        <input value={e.draft.zip} onChange={(v) => setEditing({ ...e, draft: { ...e.draft, zip: v.target.value } })} className={input} />
      </label>
      <label className="text-xs font-medium text-muted">
        Label (optional)
        <input value={e.draft.label} onChange={(v) => setEditing({ ...e, draft: { ...e.draft, label: v.target.value } })} placeholder="e.g. Corporate UPS" className={input} />
      </label>
      <div className="flex gap-2 sm:col-span-4">
        <Button type="submit" size="sm" disabled={save.isPending}>{save.isPending ? "Saving…" : "Save account"}</Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => { setEditing(null); setError(null); }}>Cancel</Button>
      </div>
    </form>
  );

  return (
    <section className="mt-8" aria-labelledby="client-ship-accounts">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 id="client-ship-accounts" className="text-lg font-semibold tracking-[-0.4px] text-ink">Shipping accounts</h2>
          <p className="mt-0.5 text-xs text-muted">The client's own carrier accounts, for orders shipped on their account.</p>
        </div>
        {canEdit && editing?.id !== "new" && (
          <Button variant="outline" onClick={() => { setError(null); setEditing({ id: "new", draft: EMPTY }); }}>
            <Plus className="h-4 w-4" aria-hidden /> Add shipping account
          </Button>
        )}
      </div>
      {editing?.id === "new" && <div className="mb-3">{form(editing)}</div>}
      {error && <p className="mb-3 text-sm text-rust-600">{error}</p>}
      {q.isLoading ? (
        <div className="h-16 animate-pulse rounded-md bg-white" aria-label="Loading shipping accounts" />
      ) : rows.length === 0 ? (
        <p className="rounded-md border border-rule bg-white px-5 py-6 text-center text-sm text-muted">No shipping accounts yet.</p>
      ) : (
        <ul className="divide-y divide-[#e7e7e4] overflow-hidden rounded-md border border-rule bg-white">
          {rows.map((r) =>
            editing?.id === r.id ? (
              <li key={r.id} className="p-3">{form(editing)}</li>
            ) : (
              <li key={r.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm">
                <span className="text-ink">
                  <span className="font-medium">{carrierLabel(r.carrier)}</span> · {r.accountNumber} · ZIP {r.accountPostalCode}
                  {r.label ? <span className="text-muted"> · {r.label}</span> : null}
                  {r.isActive === false ? <span className="text-muted"> · not in use</span> : null}
                </span>
                {canEdit && (
                  <span className="flex items-center gap-3 text-xs">
                    {confirmDelete === r.id ? (
                      <>
                        <span className="text-ink">Delete this account?</span>
                        <button type="button" disabled={remove.isPending} onClick={() => remove.mutate(r.id)} className="font-semibold text-rust-600 hover:underline">Yes, delete</button>
                        <button type="button" onClick={() => setConfirmDelete(null)} className="text-muted hover:text-ink">No</button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => { setError(null); setEditing({ id: r.id, draft: { carrier: r.carrier, accountNumber: r.accountNumber, zip: r.accountPostalCode, label: r.label ?? "" } }); }}
                          className="font-medium text-muted hover:text-ink"
                        >
                          Edit
                        </button>
                        <button type="button" onClick={() => { setError(null); setConfirmDelete(r.id); }} className="font-medium text-muted hover:text-ink">Delete</button>
                      </>
                    )}
                  </span>
                )}
              </li>
            ),
          )}
        </ul>
      )}
    </section>
  );
}
